import moment from "moment";

/** A `date:` filter token, extracted from the journal filter text. */
export type DateFilterRange = {
	/** the raw token text, eg. "date:2026-05-01+-5" */
	token: string;
	/** the date given by the user, normalized to YYYY-MM-DD */
	dateStr: string;
	/** tolerance in days (0 = only the given day) */
	toleranceDays: number;
	/** 0 = on either side (+-), 1 = only on/after the given date (+), -1 = only on/before (-) */
	direction: -1|0|1;
	/** inclusive lower bound of the matched time-range (ms) */
	time_min: number;
	/** inclusive upper bound of the matched time-range (ms) */
	time_max: number;
};

const regex_dateFilterToken = /^date:(\d{4}-\d{1,2}-\d{1,2})(.*)$/i;
const regex_number = /^\d+(\.\d+)?$/;

/** Parses a single filter-text token into a `DateFilterRange`, or returns null if it's not a (valid) `date:` token.
 * Accepted forms:
 * * "date:2026-05-01" -- entries on that date (in the user's local timezone)
 * * "date:2026-05-01+-5" -- entries within 5 days of that date (on either side)
 * * "date:2026-05-01+5" -- entries from that date, up to 5 days after it
 * * "date:2026-05-01-5" -- entries from that date, up to 5 days before it
 */
export function ParseDateFilterToken(token: string): DateFilterRange|n {
	const match = regex_dateFilterToken.exec(token);
	if (match == null) return null;
	const dateStr = match[1];
	const suffix = match[2];

	const date = moment(dateStr, "YYYY-M-D", true);
	if (!date.isValid()) return null;

	let toleranceDays = 0;
	let direction: -1|0|1 = 0;
	if (suffix.length) {
		let numberStr: string;
		if (suffix.startsWith("+-")) {
			direction = 0;
			numberStr = suffix.slice(2);
		} else if (suffix.startsWith("+")) {
			direction = 1;
			numberStr = suffix.slice(1);
		} else if (suffix.startsWith("-")) {
			direction = -1;
			numberStr = suffix.slice(1);
		} else {
			// unrecognized suffix (eg. "date:2026-05-01abc"); leave the token to be treated as plain search-text
			return null;
		}
		if (!regex_number.test(numberStr)) return null;
		toleranceDays = Number(numberStr);
	}

	// note: we use moment (rather than adding ms offsets) so that day-lengths are handled correctly across DST changes
	const dayStart = date.startOf("day");
	// the range extends earlier than the given date unless the token is "+N"-only, and later unless it's "-N"-only
	const rangeStart = direction == 1 ? dayStart.clone() : dayStart.clone().subtract(toleranceDays, "days");
	const rangeEndExclusive = direction == -1 ? dayStart.clone().add(1, "days") : dayStart.clone().add(toleranceDays + 1, "days");

	return {
		token,
		dateStr: dayStart.format("YYYY-MM-DD"),
		toleranceDays,
		direction,
		time_min: rangeStart.valueOf(),
		time_max: rangeEndExclusive.valueOf() - 1,
	};
}

export type ParsedJournalFilterText = {
	/** the filter text with any recognized `date:` tokens removed (so the remainder can be word-matched) */
	text_forWords: string;
	dateRanges: DateFilterRange[];
};

/** Splits the journal filter text into its word-search part and its `date:` range part. */
export function ParseJournalFilterText(text: string): ParsedJournalFilterText {
	const dateRanges = [] as DateFilterRange[];
	const wordTokens = [] as string[];
	for (const token of text.split(/\s+/)) {
		if (!token.length) continue;
		const range = ParseDateFilterToken(token);
		if (range) dateRanges.push(range);
		else wordTokens.push(token);
	}
	return {text_forWords: wordTokens.join(" "), dateRanges};
}

/** Returns true if the given [time_min, time_max] range overlaps at least one of the date-filters.
 * (Returns true if there are no date-filters at all.) */
export function TimeRangeMatchesDateFilters(time_min: number, time_max: number, dateRanges: DateFilterRange[]): boolean {
	if (dateRanges.length == 0) return true;
	return dateRanges.some(range=>time_min <= range.time_max && time_max >= range.time_min);
}
